'use client';
import { Autocomplete, AutocompleteOption, Box, Button, Typography, Drawer, Stack } from '@castlery/fortress';
import { Search } from '@castlery/fortress/Icons';
import {
  selectedCurrentCustomer,
  selectedCustomerName,
  setCustomer,
  useGetUsersQuery,
} from '@castlery/modules-user-domain';
import { useAppDispatch, useAppSelector } from '@castlery/shared-redux-store';
import React, { useState } from 'react';
import { useDebounce } from 'react-use';
import { usePathname } from 'next/navigation';
import PosAddNewCustomer from '../pos-add-new-customer/pos-add-new-customer';
import { trackViewContent } from '@castlery/modules-tracking-domain';
import { trackOfflineAccountSignIn, trackOfflineAtc } from '@castlery/modules-tracking-services';

export interface PosSelectCustomerContentProps {
  onChange: (value: any) => void;
  closeDrawer?: () => void;
}
export const PosSelectCustomerContent = ({ onChange, closeDrawer }: PosSelectCustomerContentProps) => {
  const [inputValue, setInputValue] = useState('');
  const [params, setParams] = useState({
    page: 1,
    q: '',
  });
  const [,] = useDebounce(
    () => {
      setParams({ q: inputValue, page: 1 });
    },
    500,
    [inputValue]
  );

  const { currentData, isFetching } = useGetUsersQuery(params, {
    skip: !params.q,
  });

  const options = currentData?.results || [];

  return (
    <>
      {/* TODO 这里没有自动聚焦？ */}
      <Autocomplete
        clearOnEscape
        autoFocus
        autoComplete
        sx={{
          width: '100%',
        }}
        options={options}
        onChange={(_, value) => {
          onChange({ value });
        }}
        variant="outlined"
        startDecorator={<Search />}
        placeholder="Search for customer"
        loading={isFetching}
        getOptionLabel={(option) => option.email}
        onInputChange={(event, value) => {
          if (event.type === 'change') {
            setInputValue(value);
          }
        }}
        filterOptions={(options, params) => {
          const filtered = options.filter((option) => {
            const name = option.firstname + option.lastname;
            return (
              option.email.toLowerCase().includes(params.inputValue.toLowerCase()) ||
              name.toLowerCase().includes(params.inputValue.toLowerCase())
            );
          });

          return filtered;
        }}
        renderOption={(props, options, { selected }) => {
          return (
            <AutocompleteOption
              {...props}
              key={options.email}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                p: 1,
                '&:not(:last-child)': {
                  borderBottom: (theme) => `1px solid ${theme.palette.brand.wheat[500]}`,
                },
              }}
            >
              <Stack
                direction={'column'}
                sx={{
                  width: '100%',
                  '&>span': {
                    width: '100%',
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                    wordBreak: 'break-all',
                  },
                }}
              >
                <Typography level="caption1">
                  {options.firstname} + {options.lastname}
                </Typography>
                <Typography level="caption1">{options.email}</Typography>
              </Stack>
            </AutocompleteOption>
          );
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 42, //留mobile上的底部安全距离
          left: 0,
          right: 0,
          m: 2,
        }}
      >
        <PosAddNewCustomer afterAddCustomer={closeDrawer} />
      </Box>
    </>
  );
};

export const PosSelectCustomer = () => {
  const clickDisabled = usePathname().includes('/checkout');
  const name = useAppSelector(selectedCustomerName);
  const customer = useAppSelector(selectedCurrentCustomer);
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const closeDrawer = () => setOpen(false);

  return (
    <React.Fragment>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingX: 1,
        }}
      >
        {customer ? (
          <Box
            role="button"
            onClick={() => {
              if (clickDisabled) return false;
              setOpen(true);
            }}
            sx={{ paddingX: { sm: 0, lg: 1 }, flex: 1 }}
          >
            <Typography level="subh2">{name}</Typography>
            <Typography level="caption2">{customer?.email}</Typography>
          </Box>
        ) : (
          <Button
            variant="tertiary"
            sx={{ p: 0, m: 0, flex: 1, justifyContent: 'flex-start' }}
            onClick={() => {
              if (clickDisabled) return false;
              setOpen(true);
            }}
          >
            <Typography
              level="caption1"
              sx={{ textAlign: 'left' }}
              startDecorator={<Search sx={{ '--Icon-fontSize': '24px' }} />}
            >
              Search for a customer
            </Typography>
          </Button>
        )}
        <PosAddNewCustomer iconButton defaultStep="Add New Customer" afterAddCustomer={closeDrawer} />
      </Stack>
      <Drawer title="Select Customer" showCloseButton open={open} onClose={() => setOpen(false)}>
        <PosSelectCustomerContent
          onChange={async ({ value }) => {
            if (value) {
              dispatch(setCustomer(value));
              await dispatch(trackViewContent());
              await dispatch(trackOfflineAccountSignIn());
              await dispatch(trackOfflineAtc());
              setOpen(false);
            }
          }}
          closeDrawer={closeDrawer}
        />
      </Drawer>
    </React.Fragment>
  );
};
